const TOKEN_KEY = 'token';
const USER_KEY = 'user';
const TEMP_TOKEN_KEY = 'impersonation_token';
const TEMP_USER_KEY = 'impersonation_user';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function isBrowser() {
  return typeof window !== 'undefined';
}

function readJson<T>(storage: StorageLike, key: string): T | null {
  const raw = storage.getItem(key);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function hasTemporaryAuthSession(): boolean {
  if (!isBrowser()) return false;
  return !!window.sessionStorage.getItem(TEMP_TOKEN_KEY);
}

export function getActiveToken(): string | null {
  if (!isBrowser()) return null;
  if (hasTemporaryAuthSession()) {
    return window.sessionStorage.getItem(TEMP_TOKEN_KEY);
  }
  return window.localStorage.getItem(TOKEN_KEY);
}

export function getActiveUser<T>(): T | null {
  if (!isBrowser()) return null;
  if (hasTemporaryAuthSession()) {
    return readJson<T>(window.sessionStorage, TEMP_USER_KEY);
  }
  return readJson<T>(window.localStorage, USER_KEY);
}

export function savePrimaryAuthSession(user: unknown, token: string): void {
  if (!isBrowser()) return;
  clearTemporaryAuthSession();
  window.localStorage.setItem(TOKEN_KEY, token);
  window.localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function saveTemporaryAuthSession(user: unknown, token: string): void {
  if (!isBrowser()) return;
  window.sessionStorage.setItem(TEMP_TOKEN_KEY, token);
  window.sessionStorage.setItem(TEMP_USER_KEY, JSON.stringify(user));
}

export function saveActiveUser(user: unknown): void {
  if (!isBrowser()) return;
  if (hasTemporaryAuthSession()) {
    window.sessionStorage.setItem(TEMP_USER_KEY, JSON.stringify(user));
    return;
  }
  window.localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearPrimaryAuthSession(): void {
  if (!isBrowser()) return;
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(USER_KEY);
}

export function clearTemporaryAuthSession(): void {
  if (!isBrowser()) return;
  window.sessionStorage.removeItem(TEMP_TOKEN_KEY);
  window.sessionStorage.removeItem(TEMP_USER_KEY);
}

export function clearActiveAuthSession(): void {
  if (hasTemporaryAuthSession()) {
    clearTemporaryAuthSession();
    return;
  }
  clearPrimaryAuthSession();
}
